import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { DiffLine } from "~/features/comments/diff";
import { cn } from "~/foundation/utils/cn";

/**
 * A unified diff, rendered (W8 §B.9 / S10).
 *
 * `dir="ltr"` on the block is not a detail: a diff is code-shaped, its `+`/`-` gutter is
 * positional, and a right-to-left page would move the gutter to the wrong side of the text it
 * marks. Colour alone never carries the meaning either — every row keeps its sign, so the
 * diff reads the same to someone who cannot distinguish the two greens.
 */
export function DiffView({ lines }: { lines: DiffLine[] }) {
  const { t } = useTranslation();

  /**
   * A diff has no ids, and repeated identical lines are normal — so a key has to carry WHICH
   * occurrence this is. Counting occurrences gives a key that survives a re-render and still
   * distinguishes two blank context lines, which the array index alone would not do stably
   * when the diff is recomputed.
   */
  const keyed = useMemo(() => {
    const seen = new Map<string, number>();
    return lines.map((line) => {
      const base = `${line.kind}:${line.text}`;
      const occurrence = (seen.get(base) ?? 0) + 1;
      seen.set(base, occurrence);
      return { line, key: `${base}#${occurrence}` };
    });
  }, [lines]);

  if (lines.length === 0) {
    return null;
  }

  return (
    <div
      dir="ltr"
      className="scrollbar-thin max-h-60 overflow-auto rounded-sm border border-divider bg-bg-secondary"
    >
      <table aria-label={t("Proposed changes")} className="w-full border-collapse text-caption">
        <tbody>
          {keyed.map(({ line, key }) => (
            <tr
              key={key}
              className={cn(
                line.kind === "add"
                  ? "bg-success/10 text-success"
                  : line.kind === "del"
                    ? "bg-danger/10 text-danger"
                    : line.kind === "header"
                      ? "text-text-tertiary"
                      : "text-text-secondary",
              )}
            >
              <td className="w-5 select-none px-1 text-center align-top opacity-70">
                {line.kind === "add" ? "+" : line.kind === "del" ? "-" : ""}
              </td>
              <td className="whitespace-pre-wrap break-words px-1 align-top">{line.text}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
